export const targetVisibilityModes = [
  "visible",
  "hidden",
  "hidden_until_completed",
  "hidden_counted",
  "teacher_only",
  "admin_only"
] as const;

export type TargetVisibilityMode = (typeof targetVisibilityModes)[number];

export type TargetResultStatus = "not_started" | "passed" | "failed" | "escalated" | "manual_review";

export interface TargetDefinition {
  id: string;
  title: string;
  safeTitle?: string;
  description?: string;
  visibility: TargetVisibilityMode;
  weight: number;
  evaluatorKey: string;
  scoring: "automatic" | "manual" | "hybrid";
  expected?: unknown;
  teacherNotes?: string;
}

export interface TargetResult {
  targetId: string;
  status: TargetResultStatus;
  completedAt?: string;
  scoreAwarded?: number;
}

export interface StudentTargetView {
  id: string;
  title: string;
  description: string;
  status: TargetResultStatus;
  hidden: boolean;
  revealedByCompletion: boolean;
}

export interface TargetVisibilitySummary {
  visibleTargets: StudentTargetView[];
  hiddenRemainingCount: number;
  totalTargets: number;
  completedTargets: number;
}

export function revealHiddenAfterCompletion(
  attemptStatus: string | null | undefined,
  targetResultsExist: boolean
): boolean {
  if (!targetResultsExist) {
    return false;
  }

  return attemptStatus === "reviewed" || attemptStatus === "closed";
}

function getResult(results: TargetResult[], targetId: string): TargetResult | undefined {
  return results.find((result) => result.targetId === targetId);
}

function isCompleted(result: TargetResult | undefined): boolean {
  return result?.status === "passed" || result?.status === "escalated" || result?.status === "manual_review";
}

function resultStatus(result: TargetResult | undefined): TargetResultStatus {
  return result?.status ?? "not_started";
}

function safeTargetTitle(target: TargetDefinition): string {
  return target.safeTitle || target.title;
}

function safeTargetDescription(target: TargetDefinition): string {
  return target.description || "";
}

export function buildStudentTargetSummary(
  targets: TargetDefinition[],
  results: TargetResult[],
  options: { countHiddenTargets?: boolean; revealHiddenAfterCompletion?: boolean } = {}
): TargetVisibilitySummary {
  const countHiddenTargets = options.countHiddenTargets ?? true;
  const revealAfterCompletion = options.revealHiddenAfterCompletion ?? false;
  const visibleTargets: StudentTargetView[] = [];
  let hiddenRemainingCount = 0;
  let completedTargets = 0;

  for (const target of targets) {
    const result = getResult(results, target.id);
    const completed = isCompleted(result);

    if (completed) {
      completedTargets += 1;
    }

    if (target.visibility === "visible") {
      visibleTargets.push({
        id: target.id,
        title: safeTargetTitle(target),
        description: safeTargetDescription(target),
        status: resultStatus(result),
        hidden: false,
        revealedByCompletion: false
      });
      continue;
    }

    if (target.visibility === "hidden_until_completed" && (completed || revealAfterCompletion)) {
      visibleTargets.push({
        id: target.id,
        title: target.title,
        description: safeTargetDescription(target),
        status: resultStatus(result),
        hidden: true,
        revealedByCompletion: true
      });
      continue;
    }

    if (
      countHiddenTargets &&
      !revealAfterCompletion &&
      !completed &&
      (target.visibility === "hidden_until_completed" || target.visibility === "hidden_counted")
    ) {
      hiddenRemainingCount += 1;
    }
  }

  return {
    visibleTargets,
    hiddenRemainingCount,
    totalTargets: targets.length,
    completedTargets
  };
}

export function assertNoHiddenTargetLeak(studentView: TargetVisibilitySummary): void {
  const serialized = JSON.stringify(studentView).toLowerCase();
  const forbiddenTokens = ["expected", "evaluatorkey", "teachernotes"];

  for (const token of forbiddenTokens) {
    if (serialized.includes(token)) {
      throw new Error(`Student target view leaks hidden field: ${token}`);
    }
  }
}
